"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { DocsLayout } from "@/components/cli/DocsLayout";
import { DocsBreadcrumb } from "@/components/cli/DocsBreadcrumb";
import { DocsTableOfContents, type TocItem } from "@/components/cli/DocsTableOfContents";
import { CodeBlock } from "@/components/cli/CodeBlock";
import type { DocsSidebarItem } from "@/components/cli/DocsSidebar";

// Sidebar navigation
const sidebarItems: DocsSidebarItem[] = [
  {
    title: "Get started",
    defaultOpen: true,
    items: [
      { title: "Installation", href: "/cli" },
      { title: "Authentication", href: "/cli/authentication" },
    ],
  },
  {
    title: "Usage",
    defaultOpen: true,
    items: [
      { title: "Claude Code", href: "/cli/usage" },
      { title: "Cursor", href: "/cli/usage/cursor", disabled: true },
      { title: "Kiro", href: "/cli/usage/kiro", disabled: true },
      { title: "Gemini", href: "/cli/usage/gemini", disabled: true },
      { title: "Copilot", href: "/cli/usage/copilot", disabled: true },
    ],
  },
];

// Table of contents items
const tocItems: TocItem[] = [
  { id: "why-authenticate", title: "Why authenticate?" },
  { id: "login", title: "Login" },
  { id: "logout", title: "Logout" },
  { id: "whats-next", title: "What's next?" },
];

export default function AuthenticationPage() {
  const [activeTocId, setActiveTocId] = useState<string>(tocItems[0]?.id);

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 120;

      let currentSection = tocItems[0]?.id;

      for (const item of tocItems) {
        const element = document.getElementById(item.id);
        if (element) {
          const { offsetTop } = element;
          if (scrollPosition >= offsetTop) {
            currentSection = item.id;
          }
        }
      }

      setActiveTocId(currentSection);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <DocsLayout
      sidebar={sidebarItems}
      rightSidebar={
        <DocsTableOfContents
          items={tocItems}
          activeId={activeTocId}
          onItemClick={setActiveTocId}
        />
      }
    >
      {/* Breadcrumb */}
      <DocsBreadcrumb
        items={[
          { label: "CLI", href: "/cli" },
          { label: "Get started" },
          { label: "Authentication" },
        ]}
        className="mb-6"
      />

      {/* Page Title */}
      <h1 className="text-3xl md:text-4xl font-bold text-text mb-4">
        Authentication
      </h1>

      {/* Introduction */}
      <p className="text-lg text-text-secondary mb-8 leading-relaxed">
        Authentication links your CLI to your Specledger account, enabling
        commands that sync and interact with your projects.
      </p>

      {/* Why authenticate */}
      <section id="why-authenticate" className="mb-12">
        <h2 className="text-2xl font-bold text-text mb-4">Why authenticate?</h2>
        <p className="text-text-secondary mb-6">
          Authentication unlocks Specledger&apos;s collaboration features. Once logged in and
          a project is created on web, your specs, plans, and tasks sync automatically - enabling
          team collaboration, project tracking, and seamless handoffs between developers.
        </p>

        <div className="p-4 bg-bg-alt rounded-xl">
          <p className="text-sm font-medium text-text mb-2">With authentication, you can:</p>
          <ul className="space-y-1.5 text-sm text-text-muted">
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              Sync data to projects created on the web app
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              Collaborate with team members in real-time
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              Track progress and manage tasks from the dashboard
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              Use <code className="px-1.5 py-0.5 bg-bg rounded text-text font-mono text-xs">/specledger.resume</code> to continue previous work
            </li>
          </ul>
        </div>
      </section>

      {/* Login */}
      <section id="login" className="mb-12">
        <h2 className="text-2xl font-bold text-text mb-4">Login</h2>
        <p className="text-text-secondary mb-6">
          Run the following command to authenticate your CLI:
        </p>

        <CodeBlock code="sl auth login" />

        <div className="mt-6 p-4 bg-bg-alt rounded-xl">
          <p className="text-sm font-medium text-text mb-2">Tips</p>
          <ul className="space-y-1.5 text-sm text-text-muted">
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              A browser window will open for authentication
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              Your credentials are stored securely on your machine
            </li>
          </ul>
        </div>
      </section>

      {/* Logout */}
      <section id="logout" className="mb-12">
        <h2 className="text-2xl font-bold text-text mb-4">Logout</h2>
        <p className="text-text-secondary mb-6">
          To sign out of the CLI:
        </p>

        <CodeBlock code="sl auth logout" />
      </section>

      {/* What's next */}
      <section id="whats-next" className="mb-12">
        <h2 className="text-2xl font-bold text-text mb-4">What&apos;s next?</h2>
        <p className="text-text-secondary mb-6">
          Now that you&apos;re authenticated, learn how to use Specledger commands in Claude Code.
        </p>

        <Link
          href="/cli/usage"
          className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
        >
          Usage with Claude Code
          <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </DocsLayout>
  );
}
